import clsx from "clsx";
import type { FeatureCollection } from "geojson";
import { type ComponentType, type RefObject, useEffect, useMemo, useRef, useState } from "react";

import { ESCAPE, registerEscape, topEscapePriority } from "../../components/escapeStack";
import { Motion, Presence, useLastDefined } from "../../components/presence";
import { tokenColor } from "../../components/theme";
import { useMediaQuery } from "../../components/useMediaQuery";
import { lang, t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { placementBasis } from "../dossierWrites";
import type { LonLat } from "../geo";
import { isEditableTarget } from "../useHistoryKeys";
import { MAP_EXIT_MS } from "../units/phaseTransition";
import { FRIEND_BLUE } from "../units/symbols";
import { useDeployment } from "../units/useDeployment";
import { canAnimate } from "./motion";
import {
  angleGap,
  centerOf,
  directionName,
  formatBearing,
  isPossible,
  nearestPossible,
  normalizeDeg,
  pointerBearing,
  ROSE,
  rumb16,
  snapBearing,
} from "./orientation";
import { type OrientationView, useOrientationView } from "./orientationView";
import { useApplyOrientation } from "./useApplyOrientation";
import { useOrientation } from "./useOrientation";

/** Anneau, flèche et poignée du mode « Orienter la manœuvre » sur la carte (docs/14 §2.14, exception C12 limitée à ce
 *  mode) : posés au centre courant du dispositif, ils tournent avec la carte pour que le nord de l'anneau reste le
 *  nord vrai. Secteurs impossibles hachurés en gris (jamais en rouge), repère du sens par défaut, flèche fantôme du
 *  sens appliqué, flèche cible bleu ami et sa poignée (mêmes états que celles du fuseau), puce de valeur, contour gris
 *  tireté de la cible. Gestes : glisser la poignée ou l'anneau (pas de 1°, aimantation à ±3° coupée par ⌥, retour au
 *  bord possible au relâcher), un second doigt abandonne le geste ; clavier : ← → (⇧ 10°), Page↑ Page↓, Début, Entrée,
 *  Échap. Le mode se ferme au changement de vue, de dossier ou de base du dispositif. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
  mapRef: RefObject<import("react-map-gl/mapbox").MapRef | null>;
  /** Cap de la carte (degrés) : l'anneau tourne de −cap. */
  bearing: number;
}

const RING_PX = 120;
const RING_COARSE_PX = 140;
const RETURN_MS = 200;
const BACK_CHIP_MS = 2600;

/** Point à l'azimut `deg` sur un cercle de rayon `r` (écran, nord en haut avant la rotation du groupe). */
function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.sin(a), cy - r * Math.cos(a)];
}

/** Secteur d'anneau entre `from` et `to` (sens horaire), rayons `r0` < `r1`. */
function sectorPath(cx: number, cy: number, r0: number, r1: number, from: number, to: number): string {
  const span = (((to - from) % 360) + 360) % 360 || 360;
  const large = span > 180 ? 1 : 0;
  const [ax, ay] = polar(cx, cy, r1, from);
  const [bx, by] = polar(cx, cy, r1, from + span);
  const [cx2, cy2] = polar(cx, cy, r0, from + span);
  const [dx, dy] = polar(cx, cy, r0, from);
  return `M${ax},${ay} A${r1},${r1} 0 ${large} 1 ${bx},${by} L${cx2},${cy2} A${r0},${r0} 0 ${large} 0 ${dx},${dy} Z`;
}

/** Secteurs impossibles (complément des secteurs possibles), en caps entiers [début, fin]. */
function impossibleArcs(possible: readonly boolean[]): [number, number][] {
  const out: [number, number][] = [];
  if (possible.every(Boolean)) return out;
  if (!possible.some(Boolean)) return [[0, 359]];
  const start = possible.findIndex(Boolean);
  let current: number | null = null;
  for (let k = 1; k <= 360; k++) {
    const deg = (start + k) % 360;
    if (!possible[deg] && current === null) current = deg;
    else if (possible[deg] && current !== null) {
      out.push([current, (deg + 359) % 360]);
      current = null;
    }
  }
  return out;
}

/** Ferme le mode au changement de vue, de dossier ou de base du dispositif (`trackHistoryContext`). */
function useLifecycle(view: OrientationView | null) {
  const open = useOrientation((s) => s.open);
  const { data } = useDeployment();
  useEffect(() => {
    if (!open) return;
    const ui = useUi.getState();
    const start = { view: ui.view, exercise: ui.exerciseId, basis: ui.deploymentParams ? placementBasis(ui.deploymentParams) : null };
    return useUi.subscribe((state) => {
      const basis = state.deploymentParams ? placementBasis(state.deploymentParams) : null;
      if (state.view !== start.view || state.exerciseId !== start.exercise || basis !== start.basis) useOrientation.getState().close();
    });
  }, [open]);
  // Dispositif sans état d'orientation (CENTAC, terrain libre, fuseau antérieur) : rien à orienter. Dossier changé
  // pendant le mode sans écriture d'orientation (autre onglet, fiche restée ouverte) : l'aperçu repart du nouveau
  // déploiement, le sens visé est gardé (sinon l'aperçu disparaîtrait sans rien dire, la flèche restant affichée).
  useEffect(() => {
    const store = useOrientation.getState();
    if (!open || !data || store.pending) return;
    if (!view) store.close();
    else if (data.exercise_version != null && data.exercise_version !== store.version) store.rebase(data.exercise_version);
  }, [open, data, view]);
}

/** Action d'un appui clavier en mode orientation (fonction pure, touches lues sur `e.key`, sûr en AZERTY) : nouveau
 *  sens visé, « appliquer », ou null (appui laissé aux autres). ← → : ∓1° (⇧ : ±10°) ; Page↑ Page↓ : direction
 *  suivante ou précédente de la rose ; Début : sens par défaut ; Entrée : appliquer (sauf sur un bouton ou un lien,
 *  qui agit lui-même). Échap passe par la pile partagée, ⌘Z par `useHistoryKeys`. */
export function orientationKeyAction(
  e: Pick<KeyboardEvent, "key" | "shiftKey" | "metaKey" | "ctrlKey" | "altKey" | "target">,
  target: number,
  template: number,
): { kind: "target"; value: number } | { kind: "apply" } | null {
  if (e.metaKey || e.ctrlKey) return null;
  const step = e.shiftKey ? 10 : 1;
  switch (e.key) {
    case "ArrowLeft":
      return { kind: "target", value: normalizeDeg(target - step) };
    case "ArrowRight":
      return { kind: "target", value: normalizeDeg(target + step) };
    case "PageUp":
      return { kind: "target", value: ROSE.find((d) => d > target + 0.5) ?? 0 };
    case "PageDown":
      return { kind: "target", value: [...ROSE].reverse().find((d) => d < target - 0.5) ?? 315 };
    case "Home":
      return { kind: "target", value: template };
    case "Enter": {
      const on = e.target as { closest?: (selector: string) => unknown } | null;
      if (on && typeof on.closest === "function" && on.closest("button, a, [role='button'], [role='checkbox']")) return null;
      return { kind: "apply" };
    }
    default:
      return null;
  }
}

/** Gestes clavier du mode : ignorés pendant une saisie ou sous une fenêtre modale. */
function useKeys(view: OrientationView | null, apply: (view: OrientationView | null) => void) {
  const open = useOrientation((s) => s.open);
  const viewRef = useRef(view);
  viewRef.current = view;
  useEffect(() => {
    if (!open) return;
    const offEscape = registerEscape(ESCAPE.editing, () => {
      if (!useOrientation.getState().open || useOrientation.getState().pending) return false;
      useOrientation.getState().cancel();
      return true;
    });
    const onKey = (e: KeyboardEvent) => {
      const store = useOrientation.getState();
      const current = viewRef.current;
      if (!store.open || !current || store.pending || e.defaultPrevented) return;
      if (topEscapePriority() >= ESCAPE.modal) return;
      const focused = typeof document !== "undefined" ? document.activeElement : null;
      if (isEditableTarget(e.target) || isEditableTarget(focused)) return;
      const action = orientationKeyAction(e, current.target, current.model.template);
      if (!action) return;
      e.preventDefault();
      if (action.kind === "apply") apply(current);
      else store.setTarget(action.value);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      offEscape();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, apply]);
}

export function OrientationOverlay({ Source, Layer, Marker, mapRef, bearing }: EngineParts) {
  const view = useOrientationView();
  const open = useOrientation((s) => s.open);
  const { apply } = useApplyOrientation();
  useLifecycle(view);
  useKeys(view, apply);
  const shown = open && view !== null;
  const last = useLastDefined(view);
  const coarse = useMediaQuery("(pointer: coarse)");
  const ring = coarse ? RING_COARSE_PX : RING_PX;
  // Cap exact de la carte (l'état de MapCanvas est arrondi à 5°), suivi à chaque image d'une rotation (boussole,
  // easeTo, inertie après relâchement) : le nord de l'anneau reste le nord vrai.
  const [mapBearing, setMapBearing] = useState<number | null>(null);
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!shown || !map) return;
    const follow = () => setMapBearing(map.getBearing());
    follow();
    map.on("rotate", follow);
    return () => {
      map.off("rotate", follow);
    };
  }, [shown, mapRef]);

  // Gestes de la carte au doigt : rotation et inclinaison coupées pendant le mode ; déplacement et zoom gardés.
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!shown || !map) return;
    map.touchZoomRotate.disableRotation();
    const pitch = map.touchPitch.isEnabled();
    map.touchPitch.disable();
    return () => {
      map.touchZoomRotate.enableRotation();
      if (pitch) map.touchPitch.enable();
    };
  }, [shown, mapRef]);

  const target = useMemo<FeatureCollection | null>(() => {
    const box = last?.model.target;
    if (!box) return null;
    const [w, s, e, n] = box;
    return {
      type: "FeatureCollection",
      features: [
        { type: "Feature", properties: {}, geometry: { type: "LineString", coordinates: [[w, s], [e, s], [e, n], [w, n], [w, s]] } },
      ],
    };
  }, [last]);

  if (!last) return null;
  const center = centerOf(last.model.current);
  return (
    <>
      {target && (
        <Source id="gx-orientation-target" type="geojson" data={target}>
          <Layer
            id="orientation-target"
            type="line"
            layout={{ visibility: shown ? "visible" : "none" }}
            paint={{
              "line-color": tokenColor("ink-muted"),
              "line-width": 1,
              "line-dasharray": [3, 3],
              "line-opacity": shown ? 0.8 : 0,
              "line-opacity-transition": { duration: MAP_EXIT_MS },
            }}
          />
        </Source>
      )}
      <Marker longitude={center[0]} latitude={center[1]} anchor="center" style={{ zIndex: 2 }}>
        <Presence show={shown}>
          <Motion origin="zoom">
            <OrientationRing
              view={last}
              center={center}
              ring={ring}
              mapBearing={mapBearing ?? mapRef.current?.getMap()?.getBearing() ?? bearing}
              mapRef={mapRef}
              coarse={coarse}
            />
          </Motion>
        </Presence>
      </Marker>
    </>
  );
}

function OrientationRing({
  view,
  center,
  ring,
  mapBearing,
  mapRef,
  coarse,
}: {
  view: OrientationView;
  center: LonLat;
  ring: number;
  mapBearing: number;
  mapRef: EngineParts["mapRef"];
  coarse: boolean;
}) {
  const setTarget = useOrientation((s) => s.setTarget);
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState(false);
  const [grab, setGrab] = useState(false);
  const [back, setBack] = useState<number | null>(null);
  const language = lang() === "en" ? "en" : "fr";
  const size = 2 * (ring + 48);
  const c = size / 2;
  const viewRef = useRef(view);
  viewRef.current = view;
  // Centre fixé à l'entrée du mode ; lu par les écouteurs sans les reposer à chaque rendu (le geste continue).
  const centerRef = useRef(center);
  centerRef.current = center;

  // Glisser la poignée ou l'anneau : écouteurs posés sur le SVG lui-même (phase cible), pour que la carte, qui
  // écoute son conteneur, ne se déplace pas pendant le geste.
  useEffect(() => {
    const svg = svgRef.current;
    const map = mapRef.current?.getMap();
    if (!svg || !map) return;
    let pointer: number | null = null;
    let startTarget = 0;
    let lastSnap: number | null = null;
    const grabbable = (e: Event) => e.target instanceof Element && e.target.closest("[data-grab]") !== null;
    const stop = (e: Event) => {
      if (grabbable(e) || pointer !== null) e.stopPropagation();
    };
    const bearingAt = (e: PointerEvent): number => {
      const rect = map.getCanvasContainer().getBoundingClientRect();
      const at = map.unproject([e.clientX - rect.left, e.clientY - rect.top]);
      return pointerBearing(centerRef.current, [at.lng, at.lat]);
    };
    const move = (e: PointerEvent) => {
      const current = viewRef.current;
      const targets = [...ROSE, current.model.template, normalizeDeg(current.model.template + 180), current.model.current.bearing];
      const { bearing, snapped } = snapBearing(bearingAt(e), targets, e.altKey);
      if (snapped !== null && snapped !== lastSnap) navigator.vibrate?.(10);
      lastSnap = snapped;
      setTarget(bearing);
    };
    // Un second doigt, n'importe où sur la page, abandonne le geste : le sens revient à celui d'avant.
    const other = (e: PointerEvent) => {
      if (pointer === null || e.pointerId === pointer) return;
      pointer = null;
      setGrab(false);
      setTarget(startTarget);
      window.removeEventListener("pointerdown", other, true);
    };
    const down = (e: PointerEvent) => {
      if (pointer !== null || !grabbable(e) || useOrientation.getState().pending) return;
      e.stopPropagation();
      e.preventDefault();
      pointer = e.pointerId;
      startTarget = viewRef.current.target;
      lastSnap = null;
      setGrab(true);
      setBack(null);
      svg.setPointerCapture?.(e.pointerId);
      window.addEventListener("pointerdown", other, true);
      move(e);
    };
    const moveListener = (e: PointerEvent) => {
      if (e.pointerId !== pointer) return;
      e.stopPropagation();
      move(e);
    };
    const up = (e: PointerEvent) => {
      if (e.pointerId !== pointer) return;
      pointer = null;
      window.removeEventListener("pointerdown", other, true);
      setGrab(false);
      svg.releasePointerCapture?.(e.pointerId);
      const current = viewRef.current;
      if (isPossible(current.feasibility.possible, current.target) || !current.reason) return;
      // Relâché dans un secteur impossible : retour au bord possible le plus proche.
      const edge = nearestPossible(current.feasibility.possible, current.target);
      if (edge === null) return;
      setBack(edge);
      window.setTimeout(() => setBack(null), BACK_CHIP_MS);
      if (!canAnimate()) {
        setTarget(edge);
        return;
      }
      const from = current.target;
      const span = ((((edge - from + 180) % 360) + 360) % 360) - 180;
      const started = performance.now();
      const step = (now: number) => {
        const k = Math.min(1, (now - started) / RETURN_MS);
        setTarget(normalizeDeg(from + span * k * (2 - k)));
        if (k < 1) requestAnimationFrame(step);
        else setTarget(edge);
      };
      requestAnimationFrame(step);
    };
    svg.addEventListener("pointerdown", down);
    svg.addEventListener("pointermove", moveListener);
    svg.addEventListener("pointerup", up);
    svg.addEventListener("pointercancel", up);
    svg.addEventListener("mousedown", stop);
    svg.addEventListener("touchstart", stop, { passive: true });
    svg.addEventListener("dblclick", stop);
    svg.addEventListener("click", stop);
    return () => {
      svg.removeEventListener("pointerdown", down);
      svg.removeEventListener("pointermove", moveListener);
      svg.removeEventListener("pointerup", up);
      svg.removeEventListener("pointercancel", up);
      svg.removeEventListener("mousedown", stop);
      svg.removeEventListener("touchstart", stop);
      svg.removeEventListener("dblclick", stop);
      svg.removeEventListener("click", stop);
      window.removeEventListener("pointerdown", other, true);
    };
  }, [mapRef, setTarget]);

  const targetDeg = view.target;
  const outside = Boolean(view.reason);
  const [hx, hy] = polar(c, c, ring, targetDeg);
  const [gx, gy] = polar(c, c, ring, view.model.current.bearing);
  const [tipX, tipY] = polar(c, c, ring - 10, targetDeg);
  const head = (deg: number, r: number): string => {
    const [tx, ty] = polar(c, c, r, deg);
    const [lx, ly] = polar(c, c, r - 11, deg - 4.5);
    const [rx, ry] = polar(c, c, r - 11, deg + 4.5);
    return `${tx},${ty} ${lx},${ly} ${rx},${ry}`;
  };
  const [dx, dy] = polar(c, c, ring + 10, view.model.template);
  const defaultMark = (() => {
    const [lx, ly] = polar(c, c, ring + 20, view.model.template - 3);
    const [rx, ry] = polar(c, c, ring + 20, view.model.template + 3);
    return `${dx},${dy} ${lx},${ly} ${rx},${ry}`;
  })();
  // Puce de valeur, près de la poignée, dans le repère de l'écran (la carte peut être tournée).
  const [chipX, chipY] = polar(c, c, ring + 30, targetDeg - mapBearing);
  const valueText = `${formatBearing(targetDeg)} · ${rumb16(targetDeg, language)}`;
  const label = t("Sens AMI → ENI");
  const spoken = { cap: formatBearing(targetDeg).replace("°", ""), direction: directionName(targetDeg, language) };
  const valueSpoken = outside ? t("{cap} degrés, {direction}, hors du camp", spoken) : t("{cap} degrés, {direction}, possible", spoken);
  const hit = coarse ? 18 : 12;
  const radius = grab ? 8 : hover ? 7 : 5.5;
  const hatchId = `gx-orientation-hatch-${coarse ? "c" : "f"}`;

  return (
    <div className="relative font-sans" style={{ width: size, height: size }}>
      <svg
        ref={svgRef}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="overflow-visible text-ink-muted"
        style={{ touchAction: "none" }}
      >
        <defs>
          <pattern id={hatchId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="currentColor" strokeWidth="1.5" />
          </pattern>
        </defs>
        <g transform={`rotate(${-mapBearing} ${c} ${c})`}>
          {/* Secteurs impossibles, hachurés en gris. */}
          {impossibleArcs(view.feasibility.possible).map(([from, to]) => (
            <path
              key={`${from}-${to}`}
              d={sectorPath(c, c, ring - 9, ring + 9, from - 0.5, to + 0.5)}
              fill={`url(#${hatchId})`}
              opacity={0.55}
            />
          ))}
          {/* Anneau fin, gris tireté, et ses huit graduations ; seul « N » est écrit. */}
          <circle cx={c} cy={c} r={ring} fill="none" stroke="currentColor" strokeWidth={1} strokeDasharray="4 4" />
          {ROSE.map((deg) => {
            const [ax, ay] = polar(c, c, ring - 5, deg);
            const [bx, by] = polar(c, c, ring + 5, deg);
            return <line key={deg} x1={ax} y1={ay} x2={bx} y2={by} stroke="currentColor" strokeWidth={deg === 0 ? 1.5 : 1} />;
          })}
          {(() => {
            const [nx, ny] = polar(c, c, ring + 17, 0);
            return (
              <text
                x={nx}
                y={ny}
                textAnchor="middle"
                dominantBaseline="central"
                className="fill-current text-meta font-semibold"
                transform={`rotate(${mapBearing} ${nx} ${ny})`}
              >
                N
              </text>
            );
          })()}
          {/* Repère creux du sens par défaut, à l'extérieur de l'anneau : contour seul, creux en thème clair comme en
              thème sombre. */}
          <polygon points={defaultMark} fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinejoin="round">
            <title>{t("Sens par défaut")}</title>
          </polygon>
          {/* Flèche fantôme du sens appliqué. */}
          {angleGap(view.model.current.bearing, targetDeg) > 0.5 && (
            <line x1={c} y1={c} x2={gx} y2={gy} stroke={FRIEND_BLUE} strokeWidth={1.5} strokeDasharray="5 4" opacity={0.55} />
          )}
          {/* Bande de l'anneau saisissable. */}
          <circle data-grab="" cx={c} cy={c} r={ring} fill="none" stroke="transparent" strokeWidth={2 * hit} style={{ cursor: "grab" }} />
          {/* Flèche cible, pointe sur l'anneau. */}
          <line x1={c} y1={c} x2={tipX} y2={tipY} stroke={FRIEND_BLUE} strokeWidth={2.5} strokeLinecap="round" />
          <polygon points={head(targetDeg, ring)} fill={FRIEND_BLUE} />
          {/* Poignée : mêmes états que celles du fuseau (rond blanc, survol, saisie avec halo). */}
          <g
            data-grab=""
            role="slider"
            tabIndex={0}
            aria-label={label}
            aria-valuemin={0}
            aria-valuemax={359}
            aria-valuenow={Math.round(targetDeg) % 360}
            aria-valuetext={valueSpoken}
            onPointerEnter={() => setHover(true)}
            onPointerLeave={() => setHover(false)}
            style={{ cursor: grab ? "grabbing" : "grab", outline: "none" }}
          >
            <circle cx={hx} cy={hy} r={hit} fill="transparent" />
            {grab && <circle cx={hx} cy={hy} r={13} fill="white" opacity={0.7} />}
            <circle cx={hx} cy={hy} r={radius} fill="white" stroke={FRIEND_BLUE} strokeWidth={2} />
          </g>
        </g>
      </svg>
      {/* Puce de valeur (« 045° · NE », « · hors du camp » en avertissement) ou retour au bord possible. */}
      <div
        className={clsx(
          "pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-md border border-line bg-surface/95 px-1.5 py-0.5 text-meta font-medium whitespace-nowrap shadow-float tabular-nums",
          back === null && outside ? "text-warn" : "text-ink",
        )}
        style={{ left: chipX, top: chipY }}
      >
        {back !== null
          ? t("Ramené à {cap} : au-delà, le dispositif sort du camp", { cap: formatBearing(back) })
          : outside
            ? `${valueText} · ${t("hors du camp")}`
            : valueText}
      </div>
    </div>
  );
}
